import { EMPTY_ARRAY, isMultileg, useSecuritiesContext, useSecurity } from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TradeHistory } from '../../../../../containers/Trading/DeepDive/TradeHistory/TradeHistory';
import { useCombinedMarketsAndAccounts } from '../../../../../hooks/useCombinedMarketsAndAccounts';
import { useExchangeMarkets } from '../../../../../hooks/useMarketsOfType';
import type { SingleSymbolPanelConfig, TradesHistoryPanelConfig } from '../../../../../types/LayoutConfig';
import { Panel } from '../../../../Layout/Components/Panel';
import { SingleSymbolPanelHeader } from './SingleSymbolPanelHeader';
import { SingleSymbolPanelSettings } from './SingleSymbolPanelSettings';

export function TradesHistoryPanel({ api, params }: IDockviewPanelProps<TradesHistoryPanelConfig>) {
  const [showSettings, setShowSettings] = useState(false);
  const { searchableSecurities, securitiesBySymbol } = useSecuritiesContext();
  const { symbol, marketAccounts, markets, customTitle, aggregation, settingsInitialOpen, orderPresetID, subAccount } =
    params;

  const security = useSecurity(symbol);
  const selectedMarkets = markets ?? EMPTY_ARRAY;
  const selectedMarketAccounts = marketAccounts ?? EMPTY_ARRAY;

  const selectedCombined = useCombinedMarketsAndAccounts(selectedMarketAccounts, selectedMarkets);

  const selectedExchangeMarkets = useExchangeMarkets(selectedCombined, false);
  const securityExchangeMarkets = useExchangeMarkets(security?.Markets ?? EMPTY_ARRAY, true);

  // Open settings panel when the panel is first added and `settingsInitialOpen` is true
  useEffect(() => {
    if (settingsInitialOpen) {
      setShowSettings(true);
      api.updateParameters({ settingsInitialOpen: false });
    }
  }, [settingsInitialOpen, api]);

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      if (!customTitle) {
        api.setTitle(`${symbol ? (securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol) : 'Recent'} Trades`);
      }
      api.updateParameters({ symbol, selectedMarketAccounts: [], selectedMarkets: [] });
    },
    [api, securitiesBySymbol, customTitle]
  );

  const handleUpdateParameters = useCallback(
    (params: Partial<SingleSymbolPanelConfig>) => {
      api.updateParameters(params);
    },
    [api]
  );

  const availableSecurities = useMemo(() => searchableSecurities.filter(s => !isMultileg(s)), [searchableSecurities]);

  const requestedMarkets = selectedExchangeMarkets.length > 0 ? selectedExchangeMarkets : securityExchangeMarkets;

  return (
    <>
      <Panel panelApi={api} data-testid="trades-history-panel" display="flex" flexDirection="column" overflow="hidden">
        <SingleSymbolPanelHeader
          security={security}
          aggregation={aggregation}
          markets={markets}
          marketAccounts={marketAccounts}
          enableUnifiedLiquidity={false}
          orderPresetID={orderPresetID}
          subAccount={subAccount}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          panelTypeLabel="Trades History"
        />
        {showSettings ? (
          <SingleSymbolPanelSettings
            onClose={() => setShowSettings(false)}
            onUpdateParameters={handleUpdateParameters}
            onSymbolChange={handleSymbolChange}
            allowUnifiedLiquidity={false}
            allowMultileg={false}
            marketAccounts={marketAccounts}
            markets={markets}
            aggregation={aggregation}
            symbol={symbol}
            availableSecurities={availableSecurities}
            orderPresetID={orderPresetID}
            subAccount={subAccount}
            enableUnifiedLiquidity={false}
            showPresets={false}
            shoeMarketAccounts={false}
          />
        ) : (
          security && <TradeHistory security={security} selectedMarkets={requestedMarkets} />
        )}
      </Panel>
    </>
  );
}
